import { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Smartphone } from "lucide-react";
import { PWA_UPDATE_EVENT } from "../lib/pwa";
import { Button, Modal } from "./ui";

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

export function PwaInstallButton() {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const isIos = useMemo(() => /iphone|ipad|ipod/i.test(navigator.userAgent), []);

  useEffect(() => {
    function handleInstallPrompt(event: Event) {
      event.preventDefault();
      setPrompt(event as InstallPromptEvent);
    }
    function handleInstalled() {
      setInstalled(true);
      setPrompt(null);
    }
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  if (installed || (!prompt && !isIos)) return null;

  async function install() {
    if (!prompt) {
      setIosHelpOpen(true);
      return;
    }
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setPrompt(null);
  }

  return (
    <>
      <button className="icon-button pwa-install-button" type="button" onClick={() => void install()} aria-label="Instalar HTZAP neste dispositivo" title="Instalar HTZAP">
        <Download size={18} />
      </button>
      <Modal open={iosHelpOpen} onClose={() => setIosHelpOpen(false)} title="Instalar HTZAP" eyebrow="Aplicativo no celular">
        <div className="pwa-ios-help">
          <span><Smartphone size={26} /></span>
          <p>No Safari, toque em <strong>Compartilhar</strong> e depois em <strong>Adicionar à Tela de Início</strong>.</p>
          <Button onClick={() => setIosHelpOpen(false)}>Entendi</Button>
        </div>
      </Modal>
    </>
  );
}

export function PwaUpdateNotice() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    function handleUpdate(event: Event) {
      const detail = (event as CustomEvent<{ registration: ServiceWorkerRegistration }>).detail;
      setRegistration(detail.registration);
    }
    window.addEventListener(PWA_UPDATE_EVENT, handleUpdate);
    return () => window.removeEventListener(PWA_UPDATE_EVENT, handleUpdate);
  }, []);

  if (!registration) return null;

  function update() {
    const worker = registration?.waiting;
    if (!worker) {
      setRegistration(null);
      return;
    }
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
    worker.postMessage({ type: "SKIP_WAITING" });
  }

  return (
    <aside className="pwa-update-notice" role="status" aria-live="polite">
      <span><RefreshCw size={18} /></span>
      <div>
        <strong>Nova versão disponível</strong>
        <small>Atualize quando puder para receber as melhorias.</small>
      </div>
      <button type="button" onClick={update}>Atualizar</button>
      <button type="button" className="pwa-update-later" onClick={() => setRegistration(null)}>Depois</button>
    </aside>
  );
}
